import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Course } from '@shared/models/course.model';
import { CoursesAPIService } from '@shared/services/coursesApi.service';
import { from } from 'rxjs';
import { filter, mergeMap, switchMap } from 'rxjs/operators';

@Component({
  selector: 'simulation-intro-settings',
  templateUrl: './simulation-intro-settings.component.html',
  styleUrls: ['./simulation-intro-settings.component.scss']
})
export class SimulationIntroSettingsComponent implements OnInit, OnChanges {
  @Input() course: Course;

  introForm: FormGroup;

  constructor(private _courseApiService: CoursesAPIService) {
    this.introForm = new FormGroup({
      introductionSceneEnabled: new FormControl(false),
      introductionSceneContent: new FormGroup({
        title: new FormControl('', { updateOn: 'blur' }),
        description: new FormControl('', { updateOn: 'blur' }),
        buttonLabel: new FormControl('', {
          validators: [Validators.required, Validators.maxLength(30)],
          updateOn: 'blur'
        })
      })
    });
  }

  ngOnInit(): void {
    this.introForm.valueChanges.pipe(
      mergeMap(() => from(Object.entries(this.introForm.controls))),
      filter(([, control]: [string, FormControl]) => control.dirty),
      switchMap(([label, control]: [string, FormControl]) => {
        control.markAsPristine();
        return this._courseApiService.patchCourse(this.course.id, {
          op: 'replace',
          path: `information.${label}`,
          value: control.value
        });
      })
    ).subscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['course'] && !!changes['course'].currentValue) {
      this.introForm.reset(this.course.information);
      // Pre-rich-text descriptions were saved as plain text with raw line breaks; a plain
      // string has no visual line breaks once treated as HTML, so convert it once on load.
      const descriptionControl = this.introForm.get('introductionSceneContent.description');
      const htmlDescription = this._legacyPlainTextToHtml(descriptionControl?.value);
      if (htmlDescription !== descriptionControl?.value) {
        descriptionControl?.setValue(htmlDescription, { emitEvent: false });
      }
      this.toggleForm(this.introForm.controls['introductionSceneEnabled'].value);
    }
  }

  toggleForm(enabled: boolean): void {
    if (enabled) {
      this.introForm.controls['introductionSceneContent'].enable();
    } else {
      this.introForm.controls['introductionSceneContent'].disable();
    }
  }

  onDescriptionChange(html: string): void {
    const control = this.introForm.get('introductionSceneContent.description');
    control?.setValue(html);
    control?.markAsDirty();
  }

  private _legacyPlainTextToHtml(value: string): string {
    if (!value || /<[a-z][\s\S]*>/i.test(value)) return value;
    return value.replace(/\r\n|\r|\n/g, '<br>');
  }
}
